import { axios } from './axios';
import { axiosB2 } from './axiosB2';
import { handleErrors } from './utils';

export function getStatus(token) {
    if (!token) return Promise.reject('Token is needed');
    return new Promise((resolve, reject) => {
        axios
            .get(`/massive/v1/${token}`)
            .then(({ data }) => resolve(data))
            .catch(handleErrors(reject));
    });
}

/**
 * Polls GET /massive/v1/:token until the bulk job finishes (state === 5).
 * Same completion rules as EntityMenuBulkActions.startCheckBulkStatus.
 *
 * @param {string} token
 * @param {{ signal?: AbortSignal }} [options]
 * @returns {Promise<{ success: boolean, resultOK: number, resultNOK: number }>}
 */
export function pollBulkUntilComplete(token, options = {}) {
    const { signal } = options;
    let numTries = 0;
    let lapse = 1000;

    return new Promise((resolve, reject) => {
        let currentTimeoutId = null;

        const cleanup = () => {
            if (currentTimeoutId !== null) {
                clearTimeout(currentTimeoutId);
                currentTimeoutId = null;
            }
        };

        const onAbort = () => {
            cleanup();
            reject(new Error('Aborted'));
        };

        if (signal) {
            if (signal.aborted) {
                onAbort();
                return;
            }
            signal.addEventListener('abort', onAbort);
        }

        const schedule = () => {
            cleanup();
            currentTimeoutId = setTimeout(() => {
                currentTimeoutId = null;
                getStatus(token)
                    .then((data) => {
                        const resultNOK = data.resultNOK === -1 ? 0 : data.resultNOK;
                        const resultOK = data.resultOK === -1 ? 0 : data.resultOK;

                        if (data.state === 5) {
                            if (signal) signal.removeEventListener('abort', onAbort);
                            resolve({
                                success: resultNOK === 0,
                                resultOK,
                                resultNOK,
                            });
                            return;
                        }
                        numTries++;
                        if (numTries >= 10) {
                            lapse = 1000;
                        }
                        schedule();
                    })
                    .catch((err) => {
                        if (signal) signal.removeEventListener('abort', onAbort);
                        reject(err);
                    });
            }, lapse);
        };

        schedule();
    });
}

export function cancel(token) {
    if (!token) return Promise.reject('Token is needed');
    return new Promise((resolve, reject) => {
        axios
            .delete(`/massive/v1/${token}`)
            .then(({ data }) => resolve(data))
            .catch(handleErrors(reject));
    });
}

export const bulkMeetingCreation = (data) => {
    return new Promise((resolve, reject) => {
        axiosB2
            .post('BulkMeetingCreation', data)
            .then(({ data }) => {
                if (data) resolve(JSON.parse(data));
                else reject();
            })
            .catch(reject);
    });
};

export const bulkMeetingAccountsDetail = (selected) => {
    return new Promise((resolve, reject) => {
        axiosB2
            .post('BulkMeetingAccountsDetail', {
                data: JSON.stringify({
                    entity: 'accounts',
                    entityIds: selected?.map((id) => parseInt(id, 10)),
                }),
            })
            .then(({ data }) => {
                if (data) resolve(JSON.parse(data));
                else reject();
            })
            .catch(reject);
    });
};

export const bulkEntityDetails = ({ selected, entity, service }) => {
    return new Promise((resolve, reject) => {
        axiosB2
            .post('BulkEntityDetails', {
                entity,
                service,
                entityIds: selected?.map((id) => parseInt(id, 10)),
            })
            .then(({ data }) => {
                if (data) resolve(JSON.parse(data));
                else reject();
            })
            .catch(reject);
    });
};
